import React, { useCallback, useEffect, useRef, useState } from "react"
import { RouteStop, stopActionLabel } from "@/reducers/routes2gSlice"
import { getStopIcon } from "./WaypointContextMenu"
import { AlarmClock, Clock, Copy, Route, Trash2, Truck } from "lucide-react"
import { formatDuration, formatUserFriendlyDate } from "../RouteStopView"
import { Location } from "@/reducers/locationsSlice"
import MapManager from "@/components/maps/MapManager"
import { MapMouseEvent } from "maplibre-gl"
import { SidebarMenuButton } from "@/components/ui/sidebar-menu-button"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

export type WaypointDetailsMenuProps = {
  mapManagerInstance: MapManager | null
}

const getCoordinates = (location: Location) => {
  if (location.lat && location.lon) {
    const lat = parseFloat(location.lat).toFixed(5)
    const lon = parseFloat(location.lon).toFixed(5)
    return `${lat}, ${lon}`
  }
  return undefined
}

const getAddress = (stop: RouteStop) => {
  if (stop.location.formattedAddress) {
    return `${stop.location.formattedAddress} ${getCoordinates(stop.location)}`
  } else if (stop.location.countryCode && stop.location.locality) {
    return `${stop.location.countryCode}, ${stop.location.postalCode ? stop.location.postalCode : ""} ${stop.location.locality} ${getCoordinates(stop.location)}`
  }
  return ""
}

const getRemainingTimeWithBreaks = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time_with_tacho) {
    return undefined
  }
  return (
    Math.floor(
      new Date(routeStop.estimated_arrival_time_with_tacho).getTime() -
        new Date().getTime(),
    ) / 1000
  )
}

const getRemainingTime = (routeStop: RouteStop) => {
  if (!routeStop.estimated_arrival_time) {
    return 0
  }

  return (
    Math.floor(
      new Date(routeStop.estimated_arrival_time).getTime() -
        new Date().getTime(),
    ) / 1000
  )
}

// Great-circle distance in meters between the driver and a stop. Used as a
// fallback when the backend does not provide `remaining_distance` (e.g. when
// the driver is very close to the stop).
const getGeoDistanceMeters = (
  stopLocation: Location,
  driverLocation: [number, number] | null | undefined,
) => {
  if (!driverLocation) return undefined
  if (!stopLocation.lat || !stopLocation.lon) return undefined

  const stopLat = parseFloat(stopLocation.lat)
  const stopLon = parseFloat(stopLocation.lon)
  if (Number.isNaN(stopLat) || Number.isNaN(stopLon)) return undefined

  // currentLocation is stored as [lon, lat]
  const [driverLon, driverLat] = driverLocation

  const R = 6371000 // Earth radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(stopLat - driverLat)
  const dLon = toRad(stopLon - driverLon)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(driverLat)) *
      Math.cos(toRad(stopLat)) *
      Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return Math.round(R * c)
}

const WaypointDetailsMenu: React.FC<WaypointDetailsMenuProps> = ({
  mapManagerInstance,
}) => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  // Waypoint details menu state
  const [waypointDetailsMenu, setWaypointDetailsMenu] = useState<{
    visible: boolean
    position: { x: number; y: number }
    stopIndex: number
  }>({
    visible: false,
    position: { x: 0, y: 0 },
    stopIndex: -1,
  })

  // Add state to track adjusted position
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 })

  const stop = mapManagerInstance?.getStops()[waypointDetailsMenu.stopIndex]

  // Add useEffect to adjust menu position based on screen edge proximity
  useEffect(() => {
    if (!waypointDetailsMenu.visible || !menuRef.current) return

    const menuWidth = menuRef.current.offsetWidth
    const windowWidth = window.innerWidth

    // Check if menu would extend beyond right edge
    if (waypointDetailsMenu.position.x + menuWidth > windowWidth - 20) {
      // Move menu to the left of the click point
      setAdjustedPosition({
        x: waypointDetailsMenu.position.x - menuWidth,
        y: waypointDetailsMenu.position.y,
      })
    } else {
      // Use original position
      setAdjustedPosition(waypointDetailsMenu.position)
    }
  }, [waypointDetailsMenu.visible, waypointDetailsMenu.position])

  const handleWaypointDetailsMenuClose = useCallback(() => {
    setWaypointDetailsMenu((prev) => ({ ...prev, visible: false }))
    // Re-establish hover handlers when context menu is closed
    setTimeout(() => {
      mapManagerInstance?.resetHoverState()
    }, 100)
  }, [mapManagerInstance])

  const handleRemoveStop = useCallback(
    async (stopIndex: number) => {
      if (mapManagerInstance) {
        await mapManagerInstance.removeStop(stopIndex)
      }
    },
    [mapManagerInstance],
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleWaypointDetailsMenuClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleWaypointDetailsMenuClose()
      }
    }

    if (waypointDetailsMenu.visible) {
      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscape)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [handleWaypointDetailsMenuClose, waypointDetailsMenu.visible])

  const actionLabel = stopActionLabel(t, stop?.action)

  const handleRemove = () => {
    handleRemoveStop(waypointDetailsMenu.stopIndex)
    handleWaypointDetailsMenuClose()
  }

  const handleCopyAddress = async () => {
    if (!stop) return
    const address = getAddress(stop)
    if (address) {
      try {
        await navigator.clipboard.writeText(address)
        // Show a toast or notification if needed
        window.appToaster?.show({
          message: t("maps.waypointDetailsMenu.addressCopied"),
          intent: "success",
          timeout: 2000,
        })
      } catch (err) {
        console.error("Failed to copy address:", err)
        window.appToaster?.show({
          message: t("maps.waypointDetailsMenu.addressCopyFailed"),
          intent: "danger",
          timeout: 2000,
        })
      }
    }
    handleWaypointDetailsMenuClose()
  }

  const formatDistance = (distance: number) => {
    if (distance >= 1000) {
      // we want spaces. In future we need to support proper locale
      return `${Math.floor(distance / 1000).toLocaleString(getIntlLocale("fr-FR"))} km`
    }

    return `${distance} m`
  }

  const handleWaypointClick = useCallback(
    (event: MapMouseEvent) => {
      event.preventDefault()
      if (
        (mapManagerInstance?.getStops() &&
          !mapManagerInstance?.getStops().length) ||
        !mapManagerInstance
      )
        return
      // Get the waypoint feature properties
      const features = mapManagerInstance
        ?.getMapInstance()
        ?.queryRenderedFeatures(event.point, {
          layers: ["maplibre-gl-directions-waypoint-icon"],
        })

      if (features && features.length > 0) {
        const feature = features[0]
        const waypointIndex = feature.properties?.index

        if (waypointIndex !== undefined) {
          // Convert MapLibre waypoint index to our stop index
          const stops = mapManagerInstance.enrichStops(
            mapManagerInstance?.getStops(),
          )
          const shouldChangeIndex = !!mapManagerInstance.getCurrentLocation()

          const changeIndexBy = shouldChangeIndex
            ? -1 +
              (stops || []).filter((s) => s.proximity_status === "left").length
            : 0
          const stopIndex = waypointIndex + changeIndexBy

          if (stopIndex >= 0 && stops && stopIndex < stops.length) {
            // Reset hover state when context menu is shown
            mapManagerInstance.resetHoverState()

            setWaypointDetailsMenu({
              visible: true,
              position: {
                x: event.originalEvent.clientX,
                y: event.originalEvent.clientY,
              },
              stopIndex,
            })
          }
        }
      }
    },
    [mapManagerInstance],
  )

  // Use the map instance object as a dependency to detect when it changes
  const mapInstance = mapManagerInstance?.getMapInstance()

  useEffect(() => {
    if (!mapInstance) return

    const handleZoom = () => {
      if (waypointDetailsMenu.visible) {
        handleWaypointDetailsMenuClose()
      }
    }

    mapInstance.on("zoom", handleZoom)

    return () => {
      if (!mapInstance) return
      mapInstance.off("zoom", handleZoom)
    }
  }, [waypointDetailsMenu.visible, handleWaypointDetailsMenuClose, mapInstance])

  useEffect(() => {
    if (waypointDetailsMenu.visible) {
      mapManagerInstance?.resetHoverState()
      mapManagerInstance?.setShouldShowHoverPopup(false)
    } else {
      mapManagerInstance?.setShouldShowHoverPopup(true)
    }
  }, [mapManagerInstance, waypointDetailsMenu.visible])

  useEffect(() => {
    if (!mapInstance) return

    mapInstance.on(
      "click",
      "maplibre-gl-directions-waypoint-icon",
      handleWaypointClick,
    )

    mapInstance.on(
      "contextmenu",
      "maplibre-gl-directions-waypoint-icon",
      handleWaypointClick,
    )
    return () => {
      if (!mapInstance) return

      mapInstance.off(
        "click",
        "maplibre-gl-directions-waypoint-icon",
        handleWaypointClick,
      )

      mapInstance.off(
        "contextmenu",
        "maplibre-gl-directions-waypoint-icon",
        handleWaypointClick,
      )
    }
  }, [handleWaypointClick, mapInstance])

  if (!stop) return null

  if (!waypointDetailsMenu.visible) return null

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[320px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-3 text-xs font-normal">
          <div className="mt-1 flex-shrink-0">{getStopIcon(stop.action)}</div>
          <div className="flex-1 space-y-1">
            <h3>
              {actionLabel || t("maps.waypointDetailsMenu.unknownAction")}
            </h3>
            {stop.location?.companyName?.trim() && (
              <div
                className="text-sm font-semibold"
                data-testid="waypoint-company-name"
              >
                {stop.location.companyName.trim()}
              </div>
            )}
            <div className="space-y-1 text-muted-foreground">
              {stop.location?.formattedAddress}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 border-b px-3 pb-3">
        {stop.scheduled_arrival_time && !stop.is_temporary && (
          <div className="flex items-start gap-3 text-xs font-normal">
            <div className="mt-1 flex-shrink-0">
              <AlarmClock className="h-4 w-4" />
            </div>
            <div className="flex-1 space-y-1">
              <h3>
                {formatUserFriendlyDate(new Date(stop.scheduled_arrival_time))}
              </h3>
              <div className="space-y-1 text-muted-foreground">
                {t("maps.waypointDetailsMenu.scheduledArrival")}
              </div>
            </div>
          </div>
        )}

        {!!stop.remaining_time && !stop.is_temporary && (
          <div className="flex items-start gap-3 text-xs font-normal">
            <div className="mt-1 flex-shrink-0">
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1 space-y-1">
              <h3>{formatDuration(getRemainingTime(stop))}</h3>
              <div className="space-y-1 text-muted-foreground">
                {t("maps.waypointDetailsMenu.driving")}
              </div>
            </div>
          </div>
        )}

        {!!stop.remaining_time &&
          getRemainingTimeWithBreaks(stop) &&
          !stop.is_temporary && (
            <div className="flex items-start gap-3 text-xs font-normal">
              <div className="mt-1 flex-shrink-0">
                <Truck className="h-4 w-4" />
              </div>
              <div className="flex-1 space-y-1">
                <h3>
                  {formatDuration(getRemainingTimeWithBreaks(stop) as number)}
                </h3>
                <div className="space-y-1 text-muted-foreground">
                  {t("maps.waypointDetailsMenu.withBreaks")}
                </div>
              </div>
            </div>
          )}

        {(() => {
          if (stop.is_temporary) return null
          // For arrived stops the backend's `remaining_distance` is missing
          // or pinned to 0, so use a great-circle distance from the driver's
          // current location instead. For all other stops (including
          // "arriving"), show the routed `remaining_distance`.
          const distance =
            stop.proximity_status === "arrived"
              ? getGeoDistanceMeters(
                  stop.location,
                  mapManagerInstance?.getCurrentLocation() as
                    | [number, number]
                    | null
                    | undefined,
                )
              : stop.remaining_distance
          if (distance === undefined) return null
          return (
            <div className="flex items-end gap-3 text-xs font-normal">
              <div className="mt-1 flex-shrink-0">
                <Route className="h-4 w-4" />
              </div>
              <div className="flex-1 space-y-1">
                <h3>{formatDistance(distance)}</h3>
              </div>
            </div>
          )
        })()}
      </div>

      <div className="flex flex-col p-1">
        <SidebarMenuButton
          className="text-xs"
          icon={Copy}
          onClick={handleCopyAddress}
        >
          {t("maps.waypointDetailsMenu.copyAddress")}
        </SidebarMenuButton>
        <SidebarMenuButton
          className="text-xs text-destructive"
          icon={Trash2}
          onClick={handleRemove}
        >
          {t("common.remove")}
        </SidebarMenuButton>
      </div>
    </div>
  )
}

export default WaypointDetailsMenu
